import { useEffect, useMemo } from 'react';
import PhonePrefixPicker from './PhonePrefixPicker';
import useCountryLabel from '../hooks/useCountryLabel';
import styles from './HenaketPhoneInput.module.css';

/**
 * HenaketPhoneInput — composite phone-entry widget that wraps the
 * PhonePrefixPicker (country dial-code) and a native tel input for
 * the local digits. Used by the citizen-facing form preview (and
 * future runtime) when the form constructor's "phone" element is
 * rendered.
 *
 * Why this exists (vs. inline PhonePrefixPicker + tel input):
 * — Form constructor elements have a single `value` field. Splitting
 *   the E.164 string into country code + local digits, and composing
 *   them back on every keystroke, is best done in one place.
 * — CafRegister.jsx currently does this inline (lines 786-808). That
 *   logic lives here now as a reusable component. CafRegister could
 *   migrate to this component later (separate task — not in this ship).
 *
 * Value model
 * -----------
 * `value` is the full E.164 string ("+37491234567") or empty.
 * `onChange(newValue)` fires with the composed E.164 string whenever
 *  either the country code OR the local digits change. When the local
 *  digits are empty, onChange fires with "" — i.e., we don't emit
 *  "+374" with no local digits, because that's not a valid number and
 *  would trip the required-validation check.
 *
 * Country code split logic
 * ------------------------
 * For an existing value like "+37491234567" we find the country whose
 * dial_code is the longest prefix match (handles NANP +1 sharing 25
 * countries by alphabetical fallback). For empty/invalid values we
 * use the `defaultCountry` prop (admin-configured per form element)
 * or 'am' as final fallback.
 *
 * Props
 * -----
 * value          string         — E.164 phone, "+CCXXX..." or ""
 * onChange       (v: string)    — fires with new E.164 string
 * countries      CountryOption[]— from countriesApi.list() (0.36.381); each has
 *                                 {code, name, dial_code, ...}.
 *                                 Pass [] if not yet loaded — the
 *                                 input still renders, just with no
 *                                 prefix options.
 * defaultCountry string         — ISO alpha-2, default 'am'. Only
 *                                 used when value is empty/no match.
 * disabled       boolean        — disables both children
 * placeholder    string         — for the local-digits input
 * name           string         — passed to the local-digits input
 * id             string         — passed to the local-digits input
 * hasError       boolean        — passed through to PhonePrefixPicker
 *                                 for error styling
 * required       boolean        — passed to the local-digits input
 *                                 for native HTML5 validation
 */
export default function HenaketPhoneInput({
  value = '',
  onChange,
  countries = [],
  defaultCountry = 'am',
  disabled = false,
  placeholder = '',
  name,
  id,
  hasError = false,
  required = false,
  /* 0.49.73 - The country (ISO alpha-2) the number belongs to, and its
     change. Several countries share a dial code (+1, +7, +44...): the
     number alone cannot tell them apart, so the picker shows `country`
     when its code matches, and reports the country it shows (onCountry),
     also when it was worked out from the number. */
  country = '',
  onCountry,
}) {
  const countryLabel = useCountryLabel();

  // Split incoming value into country_code + local on every render.
  // We don't keep local state — the canonical state lives in `value`,
  // and the split is derived. That way, the parent controls the
  // value completely (no stale state on reset, no out-of-sync bugs
  // when the parent updates the value programmatically).
  const { countryCode, local } = useMemo(() => {
    return splitPhoneValue(value, countries, defaultCountry, country);
  }, [value, countries, defaultCountry, country]);
  // 0.49.73 - The country shown, reported when it differs from the one
  // given (a number set by a rule, a country picked).
  useEffect(() => {
    if (typeof onCountry !== 'function' || !countries.length || !countryCode) return;
    if (String(country || '').toLowerCase() !== String(countryCode).toLowerCase()) onCountry(countryCode);
  }, [countryCode, country, countries.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compose: country dial_code + local digits → "+CCXXX..."
  // 0.36.276 — Preserve the country choice even when local digits
  // are empty. Was returning '' here, but `splitPhoneValue('')`
  // returns the DEFAULT country (Armenia), so picking a different
  // country before typing any digits caused the picker to snap
  // back to Armenia on the next render — visible to the user as
  // "selection doesn't stick." Now we store "+374" alone, which
  // round-trips correctly: splitPhoneValue('+374') → { am, '' }
  // (or whatever country owns +374). The local input still shows
  // empty because `local` slice is empty. The downstream form
  // submission can still treat a dial-only value as "empty" for
  // validation purposes since it has no national digits.
  const compose = (cc, loc) => {
    const cleaned = (loc || '').replace(/[^\d]/g, '');
    const country = countries.find(c => c.code === cc);
    const dial = country?.dial_code || '';
    if (!cleaned && !dial) return '';
    if (!cleaned) return `+${dial}`;
    return `+${dial}${cleaned}`;
  };

  const handleCountryChange = (newCode) => {
    // 0.49.73 - The picked country first, so a code shared with other
    // countries keeps the one picked.
    if (typeof onCountry === 'function') onCountry(newCode);
    onChange?.(compose(newCode, local));
  };

  const handleLocalChange = (ev) => {
    // Strip non-digits as the user types. Some users paste a number
    // with separators or a "+" prefix; we keep only the digits.
    const cleaned = ev.target.value.replace(/[^\d]/g, '');
    onChange?.(compose(countryCode, cleaned));
  };

  return (
    <div className={styles.row}>
      <PhonePrefixPicker
        countries={countries}
        value={countryCode}
        onChange={handleCountryChange}
        countryLabel={countryLabel}
        hasError={hasError}
        disabled={disabled}
      />
      <input
        id={id}
        type="tel"
        className={styles.number}
        value={local}
        onChange={handleLocalChange}
        placeholder={placeholder || 'XX XXX XX XX'}
        maxLength={15}
        name={name}
        autoComplete="tel-national"
        inputMode="tel"
        disabled={disabled}
        required={required}
        aria-invalid={hasError}
      />
    </div>
  );
}


/**
 * Strip + split a saved E.164 phone like '+37491204990' into its
 * prefix country code (ISO alpha-2) and the local digits.
 *
 * Algorithm: find every country whose dial_code matches the leading
 * digits of the phone. Prefer the LONGEST matching dial_code (e.g.
 * "+1242" prefers a hypothetical "+1242" over "+1" so we don't
 * accidentally split off NANP for non-NANP countries).
 *
 * Fallback (no match, or empty input):
 *   - Empty: (defaultCountry, '') — typically 'am' = Armenia
 *   - Unrecognized prefix: (defaultCountry, whole-thing) so the user
 *     can correct it
 *
 * (This logic mirrors splitPhone() in CafRegister.jsx — duplicated
 *  here so HenaketPhoneInput is self-contained. When CafRegister
 *  migrates to this component, the duplicate can be removed.)
 */
function splitPhoneValue(saved, countries, defaultCountry, hint = '') {
  // 0.49.73 - The country given wins when the number starts with its code.
  const want = String(hint || '').toLowerCase();
  const hinted = want ? (countries || []).find(c => String(c.code).toLowerCase() === want && c.dial_code) : null;
  if (!saved) return { countryCode: hinted ? hinted.code : defaultCountry, local: '' };
  if (hinted && saved.startsWith('+') && saved.slice(1).startsWith(hinted.dial_code)) {
    return { countryCode: hinted.code, local: saved.slice(1 + hinted.dial_code.length) };
  }
  if (!saved.startsWith('+') || !countries || countries.length === 0) {
    return { countryCode: defaultCountry, local: saved.replace(/^\+/, '') };
  }
  const digits = saved.slice(1);
  const candidates = countries
    .filter(c => c.dial_code && digits.startsWith(c.dial_code))
    .sort((a, b) => b.dial_code.length - a.dial_code.length);
  if (candidates.length === 0) {
    return { countryCode: defaultCountry, local: digits };
  }
  // Within a shared dial_code (e.g. NANP +1 across 25 countries), default
  // to the alphabetically-first English name (typically USA or Canada).
  // 0.49.71 - The longest code first, then by name within one code. The
  // name sort used to undo the length sort, so a number could be read
  // with a shorter code that also matches (+1 instead of +1242).
  candidates.sort((a, b) => (b.dial_code.length - a.dial_code.length) || (a.name || '').localeCompare(b.name || ''));
  const best = candidates[0];
  return {
    countryCode: best.code,
    local: digits.slice(best.dial_code.length),
  };
}
